/* eslint-disable no-await-in-loop */
import React, { useEffect, useState } from 'react';

import { useTheme } from 'contexts/ThemeContext';
import { useUser } from 'contexts/UserContext';

import pluralize from 'utils/pluralize';
import { CATEGORY, GROUP, PRICING } from 'utils/rest';

import { ButtonCreate } from 'components/ButtonCreate';
import { Text, Tooltip } from 'components/DataDisplay';
import { FormSection } from 'components/FormSection';
import { AutocompleteTextField } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { Loading } from 'components/Loading';
import { ModalDelete } from 'components/ModalDelete';
import { SearchField } from 'components/SearchField';
import { TableData } from 'components/TableData';
import { UnauthorizedModal } from 'components/UnauthorizedModal';

import { ModalForm, ModalToDefault } from './components';

const mappingOthers = {
  insightShop: 'Zone de chalandise',
  importKey: 'Import CRM',
  clone: 'Segments clonés',
  tagFunnel: 'Tags funnel / nectars'
};

const PricingContainer = () => {
  const { adminRights, user } = useUser();
  const { themeColors } = useTheme();

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openModal, setModal] = useState('');
  const [refreshGroupPricing, setRefreshGroupPricing] = useState(false);
  const [defaultRows, setDefaultRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedGroupPricings, setSelectedGroupPricings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allPricings, setAllPricings] = useState([]);
  const [isDefaultPricing, setIsDefaultPricing] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState({});
  const isSuperAdmin = user.roles.includes('SUPERADMIN');
  const [otherPricingsCategories, setOtherPricingsCategories] = useState([]);
  const [horsTaxoOthersPricings, setHorsTaxoOthersPricings] = useState([]);

  const getToolTipText = txt => {
    return (
      <Row spacing={0}>
        <Item>
          <Text color={themeColors.dark}>{txt}</Text>
        </Item>
      </Row>
    );
  };

  const getCategoriesNames = (tbKeys, tbCategs) => {
    if (tbKeys === null) {
      return [];
    }
    return tbKeys.map(
      key => tbCategs.find(({ key: k }) => k === key)?.label || null
    );
  };

  const formatPricingCriteria = list => {
    const nbCategories = list.length;

    if (nbCategories === 0) {
      return <Text>Aucune</Text>;
    }
    const str = `${nbCategories} ${pluralize('catégorie', nbCategories)}`;

    const tooltipContent = () => {
      return (
        <Container>
          <Row spacing={0}>
            <Item>{list.map(el => getToolTipText(el))}</Item>
          </Row>
        </Container>
      );
    };
    return (
      <Container>
        <Row spacing={0}>
          <Item>
            <Tooltip title={tooltipContent()}>
              <Text color="global">{str}</Text>
            </Tooltip>
          </Item>
        </Row>
      </Container>
    );
  };

  const getOthersNames = others => {
    return others.map(other => mappingOthers[other] || other);
  };

  const formatOthers = others => {
    const nbOthers = others.length;
    if (nbOthers === 0) {
      return <Text>Aucune</Text>;
    }

    const tooltipContent = () => {
      return (
        <Container>
          <Row spacing={0}>
            <Item>{others.map(el => getToolTipText(el))}</Item>
          </Row>
        </Container>
      );
    };
    const str = `${nbOthers} ${pluralize('élément', nbOthers)}`;

    return (
      <Container>
        <Row spacing={0}>
          <Item>
            <Tooltip title={tooltipContent()}>
              <Text color="global">{str}</Text>
            </Tooltip>
          </Item>
        </Row>
      </Container>
    );
  };

  const syncOtherPricings = async ({
    criteriaCategories,
    others,
    pricings
  }) => {
    const toUpdate = pricings.reduce((acc, pricing) => {
      const newCriteriaCategories = pricing.criteriaCategories?.filter(
        item => !criteriaCategories?.some(cat => cat === item)
      );

      const newOthers = pricing.others?.filter(
        item => !others?.some(other => other === item)
      );
      const body = { _id: pricing._id };
      let needUpdate = false;
      if (newCriteriaCategories.length !== pricing.criteriaCategories.length) {
        body.criteriaCategories = newCriteriaCategories;
        needUpdate = true;
      }
      if (newOthers.length !== pricing.others.length) {
        body.others = newOthers;
        needUpdate = true;
      }
      if (needUpdate) {
        acc.push(body);
      }

      return acc;
    }, []);

    if (toUpdate.length) {
      await PRICING.updateManyPricings(toUpdate);
    }
  };

  const selectPricing = id => {
    const pricing = [...defaultRows, ...selectedGroupPricings].find(
      el => el._id === id
    );
    setSelectedPricing(pricing);
    const otherPricings = allPricings.filter(
      el =>
        el.groupId === selectedPricing?.groupId &&
        el._id !== selectedPricing._id
    );
    setOtherPricingsCategories(
      otherPricings.map(el => el.criteriaCategories).flat()
    );
    setHorsTaxoOthersPricings(otherPricings.map(el => el.others).flat());
  };

  const closeModal = () => {
    setModal(null);
    setSelectedPricing({});
    setOtherPricingsCategories([]);
    setHorsTaxoOthersPricings([]);
  };

  const addPricing = () => {
    if (adminRights?.general?.tarification?.create && isSuperAdmin) {
      setModal('modalCreate');
    } else {
      setModal('unauthorized');
    }
  };

  const handleUpdate = (id, isDefault) => {
    setIsDefaultPricing(isDefault);
    const temporarySelectedPricing = allPricings.find(
      pricing => pricing._id === id
    );
    setSelectedPricing(temporarySelectedPricing);
    const otherPricings = allPricings.filter(
      el =>
        el.groupId === temporarySelectedPricing?.groupId &&
        el._id !== temporarySelectedPricing._id
    );
    const newOtherPricingsCategories = otherPricings
      .map(el => el.criteriaCategories)
      .flat();
    const horsTaxoPricings = otherPricings.map(el => el.others).flat();
    setOtherPricingsCategories(newOtherPricingsCategories);
    setHorsTaxoOthersPricings(horsTaxoPricings);
    setModal('modalUpdate');
  };

  const getPricings = async (groupId = null, type = null) => {
    try {
      const result = await PRICING.getPricings(groupId, type);
      return result;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const getAllGroups = async () => {
    try {
      const result = await GROUP.getGroups();
      return result;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const filterRows = newRows => {
    setDefaultRows(newRows);
  };

  const handleSelectedGroup = group => {
    setSelectedGroup(group);
    setRefreshGroupPricing(true);
  };

  const getDatas = async () => {
    const [pricings, allGroups, allCategories] = await Promise.all([
      getPricings(null, 'default'),
      getAllGroups(),
      CATEGORY.getFullCategoryList()
    ]);
    setGroups(allGroups);
    const mappedCategories = allCategories.reduce((acc, el) => {
      if (el.parentKey) {
        acc.push({
          key: el.key,
          id: el._id,
          label: el.name
        });
      }
      return acc;
    }, []);
    setCategories(mappedCategories);
    if (pricings.length) {
      const defaultPricings = pricings.filter(
        pricing => pricing.type === 'default'
      );
      const mappedDefaultPricings = defaultPricings.reduce((acc, el) => {
        const defaultPricing = {
          ...el,
          // cpmCampaignBoth: `${el.cpmCrm}/${el.cpmCampaign}`,
          criteriaCategories: formatPricingCriteria(
            getCategoriesNames(el.criteriaCategories, mappedCategories)
          ),
          criteriaCategoriesArray: el.criteriaCategories,
          others: formatOthers(getOthersNames(el.others)),
          othersArray: el.others
        };
        acc.push(defaultPricing);
        return acc;
      }, []);

      const formatedPricings = pricings.reduce((acc, el) => {
        const defaultPricing = defaultRows.find(dp => dp.key === el.key);
        const formatedPricing = {
          ...el,
          name: el.name || defaultPricing.name,
          cpmCampaign:
            el.cpmCampaign !== null
              ? el.cpmCampaign
              : defaultPricing.cpmCampaign,
          cpmData: el.cpmData !== null ? el.cpmData : defaultPricing.cpmData,
          cpmCrm: el.cpmCrm !== null ? el.cpmCrm : defaultPricing.cpmCrm,
          cpmEnrichment:
            el.cpmEnrichment !== null
              ? el.cpmEnrichment
              : defaultPricing.cpmEnrichment,
          cpmExport:
            el.cpmExport !== null ? el.cpmExport : defaultPricing.cpmExport,
          criteriaCategories: el?.criteriaCategories
            ? el.criteriaCategories
            : defaultPricing?.criteriaCategoriesArray || [],
          others: el?.others ? el.others : defaultPricing?.othersArray || []
        };
        acc.push(formatedPricing);
        return acc;
      }, []);
      setDefaultRows(mappedDefaultPricings);
      setAllRows(mappedDefaultPricings);
      setAllPricings(formatedPricings);
      setRows(pricings);
    } else {
      setAllRows([]);
      setRows([]);
    }
    setRefreshGroupPricing(true);
  };

  const getGroupPricings = async (groupId, tbCategs) => {
    try {
      const groupPricings = await getPricings(groupId);
      if (groupPricings.length && defaultRows.length) {
        const mappedResults = groupPricings.reduce((acc, groupPricing) => {
          const defaultPricing = defaultRows.find(
            dp => dp.key === groupPricing.key
          );

          if (defaultPricing) {
            const mappedNames = getCategoriesNames(
              groupPricing.criteriaCategories,
              tbCategs
            );
            const updatedPricing = {
              ...groupPricing,
              name: {
                value: groupPricing.name || defaultPricing.name,
                fromGroup: !!groupPricing.name
              },
              cpmCampaign: {
                value:
                  groupPricing.cpmCampaign !== null
                    ? groupPricing.cpmCampaign
                    : defaultPricing.cpmCampaign,
                fromGroup: groupPricing.cpmCampaign !== null
              },
              cpmData: {
                value:
                  groupPricing.cpmData !== null
                    ? groupPricing.cpmData
                    : defaultPricing.cpmData,
                fromGroup: groupPricing.cpmData !== null
              },
              cpmEnrichment: {
                value:
                  groupPricing.cpmEnrichment !== null
                    ? groupPricing.cpmEnrichment
                    : defaultPricing.cpmEnrichment,
                fromGroup: groupPricing.cpmEnrichment !== null
              },
              cpmExport: {
                value:
                  groupPricing.cpmExport !== null
                    ? groupPricing.cpmExport
                    : defaultPricing.cpmExport,
                fromGroup: groupPricing.cpmExport !== null
              },
              criteriaCategories: {
                value: groupPricing.criteriaCategories
                  ? formatPricingCriteria(mappedNames)
                  : defaultPricing.criteriaCategories,
                fromGroup: !!groupPricing.criteriaCategories
              },
              others: {
                value: groupPricing.others
                  ? formatOthers(
                      groupPricing.others.map(el => getOthersNames([el]))
                    )
                  : defaultPricing.others,
                fromGroup: !!groupPricing.others
              }
            };
            acc.push(updatedPricing);
          } else {
            acc.push({
              ...groupPricing,
              name: { value: groupPricing.name, fromGroup: true },
              cpmCampaign: {
                value: groupPricing.cpmCampaign,
                fromGroup: true
              },
              cpmData: {
                value: groupPricing.cpmData,
                fromGroup: true
              },
              cpmEnrichment: {
                value: groupPricing.cpmEnrichment,
                fromGroup: true
              },
              cpmExport: { value: groupPricing.cpmExport, fromGroup: true },
              criteriaCategories: {
                value: formatPricingCriteria(groupPricing.criteriaCategories),
                fromGroup: true
              },
              others: {
                value: formatOthers(groupPricing.others),
                fromGroup: true
              }
            });
          }

          return acc;
        }, []);

        const formatedPricings = groupPricings.reduce((acc, el) => {
          const defaultPricing = defaultRows.find(dp => dp.key === el.key);
          const formatedPricing = {
            ...el,
            name: el.name || defaultPricing.name,
            cpmCampaign:
              el.cpmCampaign !== null
                ? el.cpmCampaign
                : defaultPricing.cpmCampaign,
            cpmData: el.cpmData !== null ? el.cpmData : defaultPricing.cpmData,
            cpmCrm: el.cpmCrm !== null ? el.cpmCrm : defaultPricing.cpmCrm,
            cpmEnrichment:
              el.cpmEnrichment !== null
                ? el.cpmEnrichment
                : defaultPricing.cpmEnrichment,
            cpmExport:
              el.cpmExport !== null ? el.cpmExport : defaultPricing.cpmExport,
            criteriaCategories: el?.criteriaCategories
              ? el.criteriaCategories
              : defaultPricing?.criteriaCategoriesArray || [],
            others: el?.others ? el.others : defaultPricing?.othersArray || []
          };
          acc.push(formatedPricing);
          return acc;
        }, []);

        setAllPricings(prev => [...prev, ...formatedPricings]);
        setSelectedGroupPricings(mappedResults);
      } else {
        setSelectedGroupPricings(groupPricings);
      }
    } catch (err) {
      console.error(err);
      setSelectedGroupPricings([]);
    } finally {
      setLoaded(true);
      setRefreshGroupPricing(false);
    }
  };

  const resetGroupPricing = async () => {
    const datas = selectedGroupPricings.reduce((acc, el) => {
      const body = {
        _id: el._id,
        name: null,
        cpmCampaign: null,
        cpmData: null,
        cpmCrm: null,
        cpmEnrichment: null,
        cpmExport: null,
        criteriaCategories: null,
        others: null
      };
      acc.push(body);
      return acc;
    }, []);
    await PRICING.updateManyPricings(datas);
    closeModal();
    setLoaded(false);
  };

  const handleDelete = id => {
    const temporarySelectedPricing = rows.find(pricing => pricing._id === id);
    setSelectedPricing(temporarySelectedPricing);
    allPricings
      .filter(
        el =>
          el.groupId === selectedPricing?.groupId &&
          el._id !== selectedPricing._id
      )
      .map(el => el.criteriaCategories)
      .flat();
    setModal('modalDelete');
  };

  const onCreate = async data => {
    const pricing = await PRICING.createPricing(data);

    const otherPricings = allPricings.filter(
      el => el.groupId === pricing?.groupId && el._id !== pricing?._id
    );

    await syncOtherPricings({ ...pricing, pricings: otherPricings });

    setModal(null);
    setLoaded(false);
  };

  const onUpdate = async data => {
    let payload = {
      _id: data._id
    };

    const otherPricings = allPricings.filter(
      el =>
        el.groupId === selectedPricing?.groupId &&
        el._id !== selectedPricing?._id
    );
    if (!isDefaultPricing) {
      const defaultPricing = defaultRows.find(
        pricing => pricing.key === data.key
      );

      if (data.name !== defaultPricing.name) {
        payload.name = data.name || null;
      } else {
        payload.name = '' || null;
      }
      if (Number(data.cpmCampaign) !== Number(defaultPricing.cpmCampaign)) {
        payload.cpmCampaign = data.cpmCampaign;
      } else {
        payload.cpmCampaign = null;
      }
      if (Number(data.cpmData) !== Number(defaultPricing.cpmData)) {
        payload.cpmData = data.cpmData;
      } else {
        payload.cpmData = null;
      }
      if (Number(data.cpmCrm) !== Number(defaultPricing.cpmCrm)) {
        payload.cpmCrm = data.cpmCrm;
      } else {
        payload.cpmCrm = null;
      }
      if (Number(data.cpmEnrichment) !== Number(defaultPricing.cpmEnrichment)) {
        payload.cpmEnrichment = data.cpmEnrichment;
      } else {
        payload.cpmEnrichment = null;
      }
      if (Number(data.cpmExport) !== Number(defaultPricing.cpmExport)) {
        payload.cpmExport = data.cpmExport;
      } else {
        payload.cpmExport = null;
      }
      payload.channels = data.channels;
      payload.medias = data.medias;

      if (
        data.criteriaCategories?.length !==
          defaultPricing.criteriaCategoriesArray?.length ||
        !data.criteriaCategories?.every(el =>
          defaultPricing?.criteriaCategoriesArray?.includes(el)
        )
      ) {
        const duplicatedCategories = data.criteriaCategories.filter(el =>
          otherPricingsCategories.includes(el)
        );
        if (duplicatedCategories.length) {
          for (const pricing of otherPricings) {
            if (
              pricing.criteriaCategories.some(el =>
                duplicatedCategories.includes(el)
              )
            ) {
              const newPricing = {
                _id: pricing._id,
                criteriaCategories: pricing.criteriaCategories.filter(
                  el => !duplicatedCategories.includes(el)
                )
              };
              await PRICING.updatePricing(newPricing._id, newPricing);
            }
          }
        }
        payload.criteriaCategories = data.criteriaCategories;
      } else {
        payload.criteriaCategories = null;
      }
      if (
        data.others?.length !== defaultPricing.othersArray?.length ||
        !data.others?.every(el => defaultPricing?.othersArray?.includes(el))
      ) {
        const duplicatedOthers = data.others.filter(el =>
          horsTaxoOthersPricings?.includes(el)
        );
        if (duplicatedOthers.length) {
          for (const pricing of otherPricings) {
            if (
              (Array.isArray(pricing.others) ? pricing.others : [])?.some(el =>
                duplicatedOthers.includes(el)
              )
            ) {
              const newPricing = {
                _id: pricing._id,
                others: pricing.others.filter(
                  el => !duplicatedOthers.includes(el)
                )
              };
              await PRICING.updatePricing(newPricing._id, newPricing);
            }
          }
        }
        payload.others = data.others;
      } else {
        payload.others = null;
      }
    } else {
      payload = data;
    }
    if (!payload.name) {
      await PRICING.updateManyPricings([payload]);
    } else {
      await PRICING.updatePricing(payload._id, payload);
    }

    await syncOtherPricings({ ...payload, pricings: otherPricings });
    closeModal();
    setLoaded(false);
  };

  const onDelete = async () => {
    await PRICING.deletePricing(selectedPricing._id);
    closeModal();
    setLoaded(false);
  };

  const formatGroupValue = value => {
    return (
      <Text
        style={{
          fontWeight: value.fromGroup ? 'bold' : 'normal'
        }}
      >
        {value.value}
      </Text>
    );
  };

  useEffect(() => {
    if (refreshGroupPricing) {
      if (selectedGroup?._id) {
        getGroupPricings(selectedGroup._id, categories);
      } else {
        setRefreshGroupPricing(false);
        setLoaded(true);
      }
    }

    // eslint-disable-next-line
  }, [selectedGroup, refreshGroupPricing]);

  useEffect(() => {
    if (!loaded) {
      getDatas();
    }
    // eslint-disable-next-line
  }, [loaded]);

  return (
    <Container>
      <Loading loading={!loaded}>
        <Row spacing={1}>
          <Item>
            <FormSection title="Tarification par défaut" xs={3}>
              <Row>
                <Item xs={3}>
                  <SearchField
                    onChange={filterRows}
                    datas={allRows}
                    titleHead="Recherche une tarification"
                    placeholder="Nom, Key..."
                  />
                </Item>
                <Item justify="flex-end" xs>
                  <ButtonCreate onClick={addPricing}>
                    Ajouter une tarification
                  </ButtonCreate>
                  {openModal === 'modalCreate' && (
                    <ModalForm
                      open={openModal === 'modalCreate'}
                      onClose={closeModal}
                      onValidate={onCreate}
                      actionName="Créer"
                      data={{
                        ...selectedPricing,
                        categoriesList: categories
                      }}
                    />
                  )}
                </Item>
              </Row>
              <Row>
                <Item>
                  <TableData
                    rows={defaultRows}
                    headers={[
                      { label: 'Nom', id: 'name', sortKey: 'name' },
                      { label: 'Key', id: 'key', sortKey: 'key' },
                      {
                        label: 'Catégories de critères',
                        id: 'criteriaCategories'
                      },
                      {
                        label: 'Hors taxonomie',
                        id: 'others'
                      },
                      {
                        label: 'CPM enrichissement',
                        id: 'cpmEnrichment',
                        sortKey: 'cpmEnrichment'
                      },
                      {
                        label: 'CPM campagne médias',
                        id: 'cpmCampaign',
                        sortKey: 'cpmCampaign'
                      },
                      {
                        label: 'CPM export data',
                        id: 'cpmData',
                        sortKey: 'cpmData'
                      },
                      // {
                      //   label: 'CPM campagne',
                      //   id: 'cpmCampaignBoth',
                      //   help: 'CPM campagne CRM / CPM campagne médias'
                      // },
                      {
                        label: 'CPM export audience',
                        id: 'cpmExport',
                        sortKey: 'cpmExport'
                      }
                    ]}
                    onUpdate={
                      isSuperAdmin ? id => handleUpdate(id, true) : null
                    }
                    onDelete={
                      isSuperAdmin
                        ? e => {
                            selectPricing(e);
                            if (!isSuperAdmin) {
                              setModal('unauthorized');
                              return;
                            }
                            handleDelete(e);
                          }
                        : null
                    }
                  />
                </Item>
              </Row>
            </FormSection>
          </Item>
        </Row>
        <Row spacing={1}>
          <Item>
            <FormSection title="Personnalisation organisation" xs={3}>
              <Container justify="flex-start" style={{ marginLeft: '25px' }}>
                <Row
                  style={{ maxWidth: '350px', marginTop: '6px' }}
                  justify="flex-start"
                >
                  <Item xs={10} justify="flex-start">
                    <AutocompleteTextField
                      title="Organisation"
                      placeholder="Choisissez une organisation"
                      options={groups.map(el => ({
                        ...el,
                        label: el.name,
                        value: el
                      }))}
                      formatLabel={option => {
                        return `${option.name} (${option._id})`;
                      }}
                      small
                      value={selectedGroup}
                      onChange={e => handleSelectedGroup(e)}
                    />
                  </Item>
                </Row>
                <Item justify="flex-end" xs style={{ marginTop: '40px' }}>
                  <ButtonCreate
                    onClick={() => {
                      setModal('modalResetGroup');
                    }}
                    reload
                  >
                    Réinitialiser
                  </ButtonCreate>
                </Item>
              </Container>
              {selectedGroupPricings.length > 0 && (
                <Row>
                  <Item>
                    <TableData
                      rows={selectedGroupPricings}
                      headers={[
                        {
                          label: 'Nom',
                          id: 'name',
                          sortKey: 'name',
                          format: formatGroupValue
                        },
                        { label: 'Key', id: 'key', sortKey: 'key' },
                        {
                          label: 'Catégories de critères',
                          id: 'criteriaCategories',
                          format: formatGroupValue
                        },
                        {
                          label: 'Hors taxonomie',
                          id: 'others',
                          format: formatGroupValue
                        },
                        {
                          label: 'CPM enrichissement',
                          id: 'cpmEnrichment',
                          sortKey: 'cpmEnrichment',
                          format: formatGroupValue
                        },
                        {
                          label: 'CPM campagne médias',
                          id: 'cpmCampaign',
                          sortKey: 'cpmCampaign',
                          format: formatGroupValue
                        },
                        {
                          label: 'CPM export Data',
                          id: 'cpmData',
                          sortKey: 'cpmData',
                          format: formatGroupValue
                        },
                        {
                          label: 'CPM export audience',
                          id: 'cpmExport',
                          sortKey: 'cpmExport',
                          format: formatGroupValue
                        }
                      ]}
                      onUpdate={
                        adminRights?.general.customers.advanced
                          ? id => handleUpdate(id, false)
                          : null
                      }
                    />
                  </Item>
                </Row>
              )}
            </FormSection>
          </Item>
        </Row>
      </Loading>

      {openModal === 'modalCreate' && (
        <ModalForm
          open={openModal === 'modalCreate'}
          onClose={closeModal}
          onValidate={onCreate}
          data={{ categoriesList: categories }}
          actionName="Créer"
          mappingOthers={mappingOthers}
        />
      )}
      {openModal === 'modalUpdate' && (
        <ModalForm
          open={openModal === 'modalUpdate'}
          onClose={closeModal}
          onValidate={onUpdate}
          data={{
            categoriesList: categories,
            ...selectedPricing
          }}
          actionName="Créer"
          otherPricingsCategories={otherPricingsCategories}
          mappingOthers={mappingOthers}
          horsTaxoOthersPricings={horsTaxoOthersPricings}
        />
      )}
      {openModal === 'modalDelete' && (
        <ModalDelete
          name={selectedPricing.name}
          type="cette tarification"
          open={openModal === 'modalDelete'}
          onClose={closeModal}
          onDelete={() => {
            onDelete();
          }}
        />
      )}
      {openModal === 'modalResetGroup' && (
        <ModalToDefault
          open={openModal === 'modalResetGroup'}
          onClose={closeModal}
          onConfirm={resetGroupPricing}
        />
      )}
      {openModal === 'unauthorized' && (
        <UnauthorizedModal
          open={openModal === 'unauthorized'}
          onClose={closeModal}
        />
      )}
    </Container>
  );
};

export default PricingContainer;
